import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemButton, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { sidebarItems } from '../../navigations/sidebarConfig';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = () => {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(location.pathname);

  useEffect(() => {
    setActiveKey(location.pathname);
  }, [location.pathname]);

  const handleItemClick = (path) => {
    setActiveKey(path);
    navigate(path);
  };

  const normalizePerm = (p) =>
    (p || '').toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
  const hasPerm = (perm) =>
    permissions.some((p) => normalizePerm(p) === normalizePerm(perm));
  const hasAnyPerm = (perms) =>
    perms.some((p) => permissions.some((u) => normalizePerm(u) === normalizePerm(p)));

  const getFilteredSidebarItems = () => {
    return sidebarItems.filter((item) => {
      if (hasPerm('super-admin')) return true;

      // Paths that accept multiple alternative permissions
      const multiPermMap = {
        '/users-list': ['user-list', 'user-direct-permission'],
        '/permissions/assign': ['role-view', 'user-direct-permission'],
      };
      const multiPerms = multiPermMap[item.path];
      if (multiPerms) return hasAnyPerm(multiPerms);

      // Use item.permission from sidebarConfig as single source of truth
      const required = item.permission;
      if (!required) return true;

      return hasPerm(required);
    });
  };

  const visibleItems = getFilteredSidebarItems();

  return (
    <Box
      sx={{
        width: { xs: '200px', sm: '250px' },
        height: 'calc(100vh - 97px)',
        bgcolor: 'white',
        borderRight: 1,
        borderColor: 'grey.200',
        overflow: 'hidden',
        position: 'fixed',
        top: 97,
        left: 0,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#e0e0e0', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#bdbdbd' },
        }}
      >
        <List disablePadding>
          {visibleItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleItemClick(item.path)}
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: activeKey === item.path ? 'grey.100' : 'transparent',
                  borderLeft: activeKey === item.path ? '6px solid #154A8A' : 'none',
                  '&:hover': { bgcolor: activeKey === item.path ? 'grey.100' : 'grey.50' },
                  transition: 'all 0.2s ease',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    color: activeKey === item.path ? '#154A8A' : 'grey.700',
                  }}
                >
                  <Box sx={{ color: activeKey === item.path ? '#154A8A' : 'grey.500' }}>{item.icon}</Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: activeKey === item.path ? 600 : 500,
                      color: activeKey === item.path ? '#154A8A' : 'grey.700',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 3,
          color: 'grey.500',
          fontSize: '0.75rem',
          borderTop: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'white',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption">Powered by</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#154A8A' }}>
            INOVIOTECH INC
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
